import React, { useEffect, useRef, useState } from 'react';
import Collapse from 'react-css-collapse';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as Checkmark } from '../../images/checkmark.svg';
import { ReactComponent as ArrowDown } from '../../images/icons/chevron-down.svg';
import { ReactComponent as QuestionMark } from '../../images/question-icon.svg';
import RichTextConverter from '../Common/RichText/RichTextConverter';
import PageSection from '../PageSections/PageSection';
import * as styles from './FeatureTable.module.scss';

const CollapsableSection = ({ title, tiers, features, openByDefault = false, setIsVisible, index }) => {
  const [open, setOpen] = useState(openByDefault);
  const collapseRef = useRef();

  useEffect(() => {
    const observerCallback = (entries) => {
      const [entry] = entries;
      setIsVisible(index, entry.isIntersecting);
    };
    let rootMargin = '0px';
    if (typeof window !== 'undefined') {
      const nav = document.getElementsByClassName('sticky-nav');
      if (nav && nav.length > 0) {
        rootMargin = '-84px 0px 0px 0px';
      }
    }
    const options = { rootMargin };

    const observer = new IntersectionObserver(observerCallback, options);
    const target = collapseRef.current;
    if (!target) return;

    if (target) {
      observer.observe(collapseRef.current);
    }
    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [collapseRef, index, setIsVisible]);

  return (
    <>
      <div className={styles.title} onClick={() => setOpen(!open)} style={{ '--tierCount': tiers.length }}>
        <span ref={collapseRef}>{title}</span>
        <ArrowDown className={open ? styles.flip : ''} />
      </div>
      <Collapse isOpen={open} className={styles.featuresGrid}>
        {features.map((feature, i) => {
          return (
            <div
              key={i}
              className={styles.row}
              style={{ '--tierCount': tiers.length }}
              data-analytics-section={'collapsable-tier'}
            >
              <div
                className={styles.feature}
                data-analytics-component={'tier-tooltip'}
                data-analytics-id={feature.title}
              >
                <p data-tooltip-id={`tt-${i}-${feature.title}`} data-tooltip-place={'right-end'}>
                  {feature.title} <QuestionMark />
                </p>
                <Tooltip
                  id={`tt-${i}-${feature.title}`}
                  direction={'right'}
                  className={styles.toolTipWrap}
                  classNameArrow={styles.arrow}
                  content={feature.tooltip}
                />
              </div>
              {tiers.map((tier, key) => {
                return (
                  <div className={styles.check} key={key}>
                    {feature.tiers.indexOf(tier) > -1 && <Checkmark />}
                    {feature.addOns?.indexOf(tier) > -1 && <span className={styles.addOn}>Add-on</span>}
                  </div>
                );
              })}
            </div>
          );
        })}
      </Collapse>
    </>
  );
};

const FeatureTable = ({ heading, copy, tiers, sections, noPadding = false }) => {
  const [visibleRows, setVisibleRows] = useState([]);
  const setIsVisible = async (index, isIntersecting) => {
    if (!visibleRows.includes(index) && isIntersecting) {
      await setVisibleRows((old) => [...old, index].sort());
    } else if (visibleRows.includes(index) && !isIntersecting) {
      await setVisibleRows((old) => old.filter((i) => i !== index).sort());
    }
  };

  return (
    <PageSection
      slim
      className={styles.allFeatures}
      innerClassName={noPadding ? styles.noPadding : ''}
      style={{ '--tierCount': tiers.length }}
      data-analytics-section={'feature-table'}
    >
      {heading && (
        <div className={styles.heading}>
          <h2>{heading}</h2>
          {copy && <RichTextConverter copy={copy} />}
        </div>
      )}
      <div className={styles.grid} style={{ '--tierCount': tiers.length }}>
        <div />
        {tiers.map((tier) => (
          <span
            key={tier}
            className={styles.tierHeading}
            data-analytics-component={'tier-level'}
            data-analytics-id={tier}
          >
            {tier}
          </span>
        ))}
      </div>

      {sections.map((section, key) => {
        return (
          <CollapsableSection
            key={key}
            index={key}
            setIsVisible={setIsVisible}
            visible={visibleRows[0] === key}
            title={section.title}
            tiers={tiers}
            features={section.features}
            openByDefault={key < 2}
          />
        );
      })}
    </PageSection>
  );
};

export default FeatureTable;
